import { AnimalType } from './animal';
import { FarmType } from './farm';

/**
 * Which farm a dashboard renders.
 *
 * The dashboard used to have exactly one source - whatever business unit the token was issued for - so
 * nothing had to say so. It now has two: a farm user looking at their own farm, and an admin opening a farm
 * from the farms overview. The second must not switch the admin's business unit: re-issuing the token would
 * move their whole session onto another farm just to read one screen, and put them back where they started
 * only if they remembered to switch back.
 *
 * `farmId` undefined means "the signed-in business unit", which is what the API assumes when the query
 * parameter is absent. Name and number are passed for the header only; they are never used to fetch.
 */
export interface DashboardSource {
  farmId?: string;
  farmName?: string;
  farmNumber?: string;
}

/** The signed-in user's own farm - the source the dashboard has always had. */
export const OWN_FARM_SOURCE: DashboardSource = {};

export const isOwnFarmSource = (source?: DashboardSource): boolean => !source?.farmId;

/** Where `/dashboard` lands: the farms overview for an admin, the own-farm dashboard for everyone else. */
export const DASHBOARD_PATH = '/dashboard';

/** Route for another farm's dashboard. Kept next to the model so the queue and the route cannot drift. */
export const buildFarmDashboardPath = (farmId: string): string => `${DASHBOARD_PATH}/${farmId}`;

/**
 * The overview shipped on its own path before it became what `/dashboard` shows an admin. The route survives
 * as a redirect so bookmarks and anything already linking to it still arrive somewhere sensible.
 */
export const LEGACY_FARMS_OVERVIEW_PATH = '/admin/farms-overview';

export interface DashboardViewModel {
  farmType: FarmType;
  sowData: DashboardAnimalData;
  giltData: DashboardAnimalData;
  pigletData: DashboardAnimalData;
  fatteningData: DashboardAnimalData;
  goatData: DashboardAnimalData;
  cowData: DashboardAnimalData;
  calfData: DashboardAnimalData;
}

export interface VeterinaryDashboardViewModel {
  dateTime: string;
  test: string;
}

export interface DashboardAnimalData {
  chartData: DashboardAnimalChartData[];
  dailyDose: number;
  animalType: AnimalType;
  vaccineA: string;
  vaccineB: string;
  vaccineC: string;
}

export interface DashboardAnimalChartData {
  weekNumber: number;
  countA: number;
  countB: number;
  countC: number;
}
